import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  Tooltip,
  Button,
  TextField,
  TablePagination,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Delete as DeleteIcon, Note as NoteIcon, Public as PortalIcon } from '@mui/icons-material';
import ApiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';

type NoteEntry = {
  id: string;
  content: string;
  created_at: string;
  created_by: string | null;
  created_by_name: string | null;
  // 'portal' = customer portal activity logged automatically (read-only, can't be deleted)
  source: 'staff' | 'portal';
};

const NOTE_MAX_LENGTH = 10000;

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function initials(name: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('');
}

interface Props {
  accountId: string;
}

const AccountNotesTab: React.FC<Props> = ({ accountId }) => {
  const { user } = useAuth();
  const [notes, setNotes] = useState<NoteEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [perPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newNote, setNewNote] = useState('');
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<NoteEntry | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ApiService.getAccountNotes(accountId, page + 1, perPage);
      setNotes(data.notes);
      setTotal(data.total);
    } catch {
      setError('Failed to load notes.');
    } finally {
      setLoading(false);
    }
  }, [accountId, page, perPage]);

  useEffect(() => { load(); }, [load]);

  const handlePost = async () => {
    const content = newNote.trim();
    if (!content) return;
    setPosting(true);
    setPostError(null);
    try {
      await ApiService.addAccountNote(accountId, content);
      setNewNote('');
      setPage(0);
      load();
    } catch (err: any) {
      setPostError(err?.response?.data?.detail || 'Failed to add note.');
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await ApiService.deleteAccountNote(accountId, deleteTarget.id);
      setDeleteTarget(null);
      load();
    } catch (err: any) {
      setDeleteError(err?.response?.data?.detail || 'Failed to delete note.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={10}
          placeholder="Add a note for the team…"
          value={newNote}
          onChange={(e) => setNewNote(e.target.value.slice(0, NOTE_MAX_LENGTH))}
          helperText={`${newNote.length} / ${NOTE_MAX_LENGTH} — notes cannot be edited once posted`}
          disabled={posting}
        />
        {postError && <Alert severity="error" sx={{ mt: 1 }}>{postError}</Alert>}
        <Box display="flex" justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={posting ? <CircularProgress size={14} /> : <NoteIcon />}
            onClick={handlePost}
            disabled={posting || !newNote.trim()}
          >
            {posting ? 'Posting…' : 'Add Note'}
          </Button>
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : notes.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No notes have been added to this account yet.
        </Typography>
      ) : (
        <>
          <Box display="flex" flexDirection="column" gap={1.5}>
            {notes.map((n) => {
              const isPortal = n.source === 'portal';
              return (
              <Paper
                key={n.id}
                variant="outlined"
                sx={{
                  p: 2,
                  ...(isPortal && { bgcolor: 'action.hover', borderLeft: 4, borderLeftColor: 'secondary.main' }),
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.8125rem', ...(isPortal && { bgcolor: 'secondary.main' }) }}>
                      {isPortal ? <PortalIcon fontSize="small" /> : initials(n.created_by_name)}
                    </Avatar>
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography variant="body2" fontWeight={600}>
                          {isPortal ? n.created_by_name || 'Customer portal' : n.created_by_name || 'Unknown user'}
                        </Typography>
                        {isPortal && <Chip size="small" label="Portal activity" variant="outlined" color="secondary" />}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(n.created_at)}
                      </Typography>
                    </Box>
                  </Box>
                  {user?.is_admin && !isPortal && (
                    <Tooltip title="Delete note (admin only)">
                      <IconButton size="small" onClick={() => { setDeleteError(null); setDeleteTarget(n); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Typography variant="body2" sx={{ mt: 1.5, whiteSpace: 'pre-wrap' }}>
                  {n.content}
                </Typography>
              </Paper>
              );
            })}
          </Box>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={perPage}
            rowsPerPageOptions={[perPage]}
          />
        </>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete note?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This permanently removes the note. This cannot be undone.
          </Typography>
          {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountNotesTab;
